/**
 * A confirmation for an admin action that cannot be undone from the page
 * (delete a user, a group or a site; replace a node; rotate a password): a
 * title, what will happen, and a destructive confirm. `typeToConfirm` asks
 * for a word to be typed first — replace node uses the node's label, since a
 * revoked key is never accepted again.
 */
import { useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';

export type ConfirmDialogProps = {
  readonly open: boolean;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  /** A word the user must type before confirming, or null. */
  readonly typeToConfirm: string | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onConfirm: () => void;
  readonly onOpenChange: (open: boolean) => void;
};

function ConfirmBody({ title, description, confirmLabel, typeToConfirm, pending, error, onConfirm, onOpenChange }: Omit<ConfirmDialogProps, 'open'>) {
  const [typed, setTyped] = useState('');
  const ready = typeToConfirm === null || typed.trim() === typeToConfirm;
  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      {typeToConfirm === null ? null : (
        <div className="grid gap-2">
          <Label htmlFor="confirm-word">
            Type <span className="font-mono">{typeToConfirm}</span> to confirm
          </Label>
          <Input id="confirm-word" value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} data-testid="confirm-word" />
        </div>
      )}
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={onConfirm} disabled={!ready || pending} data-testid="confirm-action">
          {pending ? 'Working…' : confirmLabel}
        </Button>
      </DialogFooter>
    </>
  );
}

export function ConfirmDialog(props: ConfirmDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>{props.open ? <ConfirmBody {...props} /> : null}</DialogContent>
    </Dialog>
  );
}
